import { useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Badge } from '@quebon/ui/components/badge'
import { Button } from '@quebon/ui/components/button'
import { CoachingEditorContext, useCoachingEditorState } from '../CoachingEditorContext'
import { MarkAllCorrectButton } from '../MarkAllCorrectButton'
import { useGradingDraft } from '@/hooks/coaching/useGradingDraft'
import type { ManualGradeCredit } from '@/types/coaching'
import {
  changedRows,
  displayedGrade,
  toggleDraft,
  type GradingSheetRow as RowModel,
} from './gradingRows'
import { GradingSheetRow, type RowSaveState } from './GradingSheetRow'

interface Props {
  draftScope: string
  presentation?: 'default' | 'plain'
  title: string
  rows: RowModel[]
  readOnly: boolean
  /** 행 1건 저장. 실패는 reject. 상위가 실제 API(override/gradeItem)에 매핑한다. */
  saveRow: (row: RowModel, grade: ManualGradeCredit) => Promise<void>
  /** 모든 저장 시도 후 한 번 호출한다. 부분 실패·재시도도 포함한다. */
  onSaveSettled?: () => void
  onRequestCoaching?: (quizId: string) => void
  /** 푸터 안내(예: 리뷰노트 첫 채점 잠금) */
  footerNotice?: ReactNode
  /** 푸터 추가 액션(예: [리뷰노트 완료]) */
  footerActions?: ReactNode
  emptyMessage?: string
  testId?: string
  /**
   * 행에 정답 문자열을 보여준다(기본 true).
   */
  showAnswer?: boolean
  /** 문제·정답 미리보기는 유지하고 부가 해설을 숨긴다(기본 false). */
  hidePreviewSolveDescription?: boolean
  /** 블록퀴즈·리뷰노트·챕터/누적 테스트에서 모든 행을 정답으로 선택한다. 저장은 별도로 수행한다. */
  allowMarkAllCorrect?: boolean
}

interface SaveProgress {
  done: number
  total: number
}

/**
 * 공용 채점표 — 워크북 채점과 같은 draft → [채점 저장] 모델.
 * - 변경 행만 표시 순서대로 **순차** 저장한다(병렬 금지: 낙관 캐시 snapshot 충돌 회피).
 * - 부분 실패 시 실패 행은 draft를 유지하고 [실패 문항 다시 저장]으로 재시도한다(멱등 upsert).
 * - draft는 scope별 메모리 store에 유지되어 모드 전환 후에도 복원된다.
 */
export function GradingSheet({
  draftScope,
  presentation = 'default',
  title,
  rows,
  readOnly,
  saveRow,
  onSaveSettled,
  onRequestCoaching,
  footerNotice,
  footerActions,
  emptyMessage,
  testId = 'grading-sheet',
  showAnswer = true,
  hidePreviewSolveDescription = false,
  allowMarkAllCorrect = false,
}: Props) {
  const { t } = useTranslation('coaching')
  const observedWorkspace = useContext(CoachingEditorContext) != null
  const plain = presentation === 'plain' || observedWorkspace
  const { draft, setDraft } = useGradingDraft(draftScope)
  const [saveState, setSaveState] = useState<Record<string, RowSaveState>>({})
  const [progress, setProgress] = useState<SaveProgress | null>(null)
  const [previewKeys, setPreviewKeys] = useState<Set<string>>(new Set())
  const saving = progress != null
  useCoachingEditorState(`sheet:${draftScope}`, Object.keys(draft).length > 0, saving)

  // 서버 갱신으로 현재값이 draft와 같아진 항목은 정리한다(저장 중에는 건드리지 않음).
  useEffect(() => {
    if (saving) return
    setDraft((current) => {
      let changed = false
      const next: Record<string, ManualGradeCredit> = {}
      for (const row of rows) {
        const value = current[row.key]
        if (value === undefined) continue
        if (value === row.current) {
          changed = true
          continue
        }
        next[row.key] = value
      }
      if (!changed && Object.keys(next).length === Object.keys(current).length) return current
      return next
    })
  }, [rows, saving, setDraft])

  const pending = useMemo(() => changedRows(rows, draft), [rows, draft])
  const failedRows = useMemo(
    () => pending.filter((row) => saveState[row.key] === 'failed'),
    [pending, saveState],
  )
  const ungradedCount = rows.filter((row) => row.current == null).length

  const markAllCorrect = () => {
    if (!allowMarkAllCorrect || readOnly || saving || rows.length === 0) return
    setDraft((current) => {
      const next = { ...current }
      for (const row of rows) {
        if (row.current === 1) delete next[row.key]
        else next[row.key] = 1
      }
      return next
    })
  }

  const runSave = async (targets: RowModel[]) => {
    if (targets.length === 0 || saving) return
    setProgress({ done: 0, total: targets.length })
    let savedCount = 0
    let failedCount = 0
    for (const row of targets) {
      const grade = draft[row.key]
      if (grade === undefined) continue
      setSaveState((current) => ({ ...current, [row.key]: 'saving' }))
      try {
        await saveRow(row, grade)
        savedCount += 1
        setSaveState((current) => ({ ...current, [row.key]: 'saved' }))
        setDraft((current) => {
          if (current[row.key] !== grade) return current
          const next = { ...current }
          delete next[row.key]
          return next
        })
      } catch {
        failedCount += 1
        setSaveState((current) => ({ ...current, [row.key]: 'failed' }))
      }
      setProgress((current) => current ? { ...current, done: current.done + 1 } : current)
    }
    setProgress(null)
    onSaveSettled?.()
    if (failedCount === 0) {
      toast.success(t('gradingMode.savedToast', { count: savedCount }))
    } else {
      toast.error(t('gradingMode.partialFailToast', { saved: savedCount, failed: failedCount }))
    }
  }

  const togglePreview = (key: string) => {
    setPreviewKeys((current) => {
      const next = new Set(current)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  const markAllCorrectButton = allowMarkAllCorrect && !readOnly && rows.length > 0 ? (
    <MarkAllCorrectButton disabled={saving} onClick={markAllCorrect} />
  ) : null

  if (plain) return <section data-testid={testId} aria-label={title} className="flex min-w-0 flex-col gap-2">
    <header className="flex flex-wrap items-center justify-between gap-2 pb-2">
      <div className="flex flex-wrap items-baseline gap-2"><h3 className="text-sm font-medium">{title}</h3><span className="text-xs text-muted-foreground">{t('gradingMode.summary', { total: rows.length, ungraded: ungradedCount })}</span></div>
      <div className="flex flex-wrap items-center gap-2">
        {readOnly ? <Badge variant="outline">{t('operationStep.readOnly')}</Badge> : pending.length > 0 ? <Badge variant="secondary" data-testid="grading-sheet-changed">{t('gradingMode.changedCount', { count: pending.length })}</Badge> : null}
        {markAllCorrectButton}
      </div>
    </header>
    {rows.length ? <ol>{rows.map((row) => <GradingSheetRow key={row.key} presentation="plain" row={row} shown={displayedGrade(row, draft)}
      changed={draft[row.key] !== undefined && draft[row.key] !== row.current} saveState={saveState[row.key] ?? 'idle'} readOnly={readOnly} disabled={saving}
      previewOpen={previewKeys.has(row.key)} showAnswer={showAnswer} hidePreviewSolveDescription={hidePreviewSolveDescription} onToggle={(grade) => setDraft((current) => toggleDraft(current, row, grade))}
      onTogglePreview={() => togglePreview(row.key)} onRequestCoaching={onRequestCoaching} />)}</ol> : <p className="py-6 text-sm text-muted-foreground">{emptyMessage ?? t('gradingMode.noRows')}</p>}
    {rows.length > 0 && (footerNotice || footerActions || !readOnly) ? <footer className="sticky bottom-[var(--admin-mobile-grading-clearance,0px)] flex flex-col gap-2 border-t border-border bg-background py-3" data-testid="grading-sheet-save-dock">
      {footerNotice ? <p role="note" className="text-xs text-muted-foreground">{footerNotice}</p> : null}
      <div className="flex flex-wrap items-center justify-end gap-2">
        {!readOnly && failedRows.length > 0 ? <Button type="button" variant="outline" className="min-h-11" disabled={saving} onClick={() => void runSave(failedRows)}>{t('gradingMode.retryFailed')}</Button> : null}
        <fieldset disabled={saving || pending.length > 0} className="flex flex-wrap gap-2">{footerActions}</fieldset>
        {!readOnly ? <Button type="button" className="min-h-11" data-testid="grading-sheet-save" disabled={saving || pending.length === 0} onClick={() => void runSave(pending)}>
          {progress ? t('gradingMode.saving', { done: progress.done, total: progress.total }) : t('gradingMode.save', { count: pending.length })}
        </Button> : null}
      </div>
    </footer> : null}
  </section>

  return (
    <section
      data-testid={testId}
      aria-label={title}
      className="rounded-xl border border-gray-200 bg-white shadow-sm"
    >
      <header className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-200 bg-gray-50 px-4 py-3">
        <h3 className="text-sm font-bold text-gray-950">{title}</h3>
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <span className="text-gray-600">
            {t('gradingMode.summary', { total: rows.length, ungraded: ungradedCount })}
          </span>
          {pending.length > 0 ? (
            <span
              className="rounded-full bg-lc-mint-100 px-2 py-0.5 font-semibold text-lc-mint-900"
              data-testid="grading-sheet-changed"
            >
              {t('gradingMode.changedCount', { count: pending.length })}
            </span>
          ) : null}
          {readOnly ? (
            <span className="rounded bg-gray-200 px-2 py-1 text-[11px] font-semibold text-gray-600">
              {t('operationStep.readOnly')}
            </span>
          ) : null}
          {markAllCorrectButton}
        </div>
      </header>

      {rows.length === 0 ? (
        <p className="p-6 text-sm text-gray-500">{emptyMessage ?? t('gradingMode.noRows')}</p>
      ) : (
        <ol className="divide-y divide-gray-100">
          {rows.map((row) => (
            <GradingSheetRow
              key={row.key}
              row={row}
              shown={displayedGrade(row, draft)}
              changed={draft[row.key] !== undefined && draft[row.key] !== row.current}
              saveState={saveState[row.key] ?? 'idle'}
              readOnly={readOnly}
              disabled={saving}
              previewOpen={previewKeys.has(row.key)}
              showAnswer={showAnswer}
              hidePreviewSolveDescription={hidePreviewSolveDescription}
              onToggle={(grade) => setDraft((current) => toggleDraft(current, row, grade))}
              onTogglePreview={() => togglePreview(row.key)}
              onRequestCoaching={onRequestCoaching}
            />
          ))}
        </ol>
      )}

      {rows.length > 0 && (footerNotice || footerActions || !readOnly) ? (
        <footer className="space-y-2 border-t border-gray-100 px-4 py-3">
          {footerNotice ? (
            <p className="text-xs text-amber-800" role="note">{footerNotice}</p>
          ) : null}
          <div className="flex flex-wrap items-center justify-end gap-2">
            {!readOnly && failedRows.length > 0 ? (
              <button
                type="button"
                disabled={saving}
                onClick={() => void runSave(failedRows)}
                className="rounded-md border border-red-300 bg-white px-3 py-1.5 text-xs font-semibold text-red-700 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {t('gradingMode.retryFailed')}
              </button>
            ) : null}
            {footerActions}
            {!readOnly ? (
              <button
                type="button"
                data-testid="grading-sheet-save"
                disabled={saving || pending.length === 0}
                onClick={() => void runSave(pending)}
                className="rounded-md bg-lc-mint-900 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-lc-mint-dark disabled:cursor-not-allowed disabled:bg-gray-300"
              >
                {progress
                  ? t('gradingMode.saving', { done: progress.done, total: progress.total })
                  : t('gradingMode.save', { count: pending.length })}
              </button>
            ) : null}
          </div>
        </footer>
      ) : null}
    </section>
  )
}
